/**
 * The cascade rules the collector applies to `objects` rows, and the small
 * lookups the Fetcher Settings screens share.
 *
 * Confirmed rules:
 *  1. Within a level, every row for a field_type is used.
 *  2. Across levels, the NARROWEST level holding a field_type wins wholesale
 *     (report select_key=subID + brand select_key=var1 -> var1 only).
 *  3. EXCEPTION - UNION_KEYS accumulate across levels instead of overriding.
 *  4. Identical field_type AND field_value at two levels is a duplicate; it is
 *     sent once, credited to the narrower level.
 *  5. A deprecated field_type (trailing x / X / **) never applies, but is kept
 *     as a dropped row so the "Not sent" list can show it.
 */
import {
  FetcherConfig,
  FetcherLookup,
  FetcherObject,
  FetcherReport,
} from "./fetcherSettingsDummyData";

/** object_type values the collector actually matches on, weakest first. */
export const LEVEL_ORDER = [
  "interface_id",
  "report_id",
  "brand_id",
  "raw_output_table_name",
];

export const LEVELS: { [key: string]: { label: string; color: string } } = {
  interface_id: { label: "INTERFACE", color: "#0E7C7B" },
  report_id: { label: "REPORT", color: "#5B54C9" },
  brand_id: { label: "BRAND", color: "#C2643B" },
  raw_output_table_name: { label: "RAW TABLE", color: "#C2643B" },
};

/**
 * Keys whose rows accumulate across levels instead of the narrowest winning.
 *  url_param                 - every level's params go into one query string.
 *  ignored_field_on_compare  - each row contributes one column to an exclusion
 *    set; overriding would drop the interface-level id/created/does_exist trio
 *    and make every row look changed on every SCD-2 run.
 */
export const UNION_KEYS = ["url_param", "ignored_field_on_compare"];

/**
 * Timeouts, retries and sleeps. Identical for every brand on an interface and
 * they never explain a broken request, so they are flagged rather than left to
 * bury the request-shaping rows.
 */
export const TUNING_KEYS = [
  "api_request_timeout",
  "api_request_sleep_time",
  "api_request_sleep_timeo",
  "api_request_max_retries",
  "login_delay_seconds",
  "page_load_timeout",
  "selenium_sleep_time",
  "selenium_url_get_retries",
  "webdriverwaittimeout",
];

/** field_type values offered as suggestions on the add/edit object form. */
export const FIELD_TYPE_SUGGESTIONS = [
  "url_param",
  "url_path",
  "base_url",
  "url_login_path",
  "url_logout_path",
  "select_key",
  "ignored_field_on_compare",
  "handle_as_date",
  "filter_field",
  "pre_filter",
  "payload",
  "request_payload",
  "api_header",
  "xml_record_header",
  "xml_terms",
  "delimiter",
  "encoding",
  "skip_header",
  "skip_footer",
  "new_field",
  "artificial_column",
  "backend_file_pattern",
  "gdrive_file_details",
  "merchant_name",
  "company_name",
  "api_request_timeout",
  "api_request_max_retries",
  "api_request_sleep_time",
  "login_delay_seconds",
  "page_load_timeout",
  "selenium_sleep_time",
  "selenium_url_get_retries",
  "webdriverwaittimeout",
];

const DEPRECATED_SUFFIX = /(x|X|\*\*)$/;

export const isDeprecatedKey = (key: string) =>
  !!key && DEPRECATED_SUFFIX.test(key);

export const isUnionKey = (key: string) => UNION_KEYS.includes(key);

export const isTuningKey = (key: string) => TUNING_KEYS.includes(key);

export const isStandardLevel = (objectType: string) =>
  LEVEL_ORDER.includes(objectType);

export const objectsFor = (
  objects: FetcherObject[],
  level: string,
  matchingId: string,
) => objects.filter((row) => row.lvl === level && row.mid === matchingId);

export interface ResolvedObject extends FetcherObject {
  /** true when the collector drops this row */
  shadow?: boolean;
  /** why it was dropped - the wording the query returns */
  why?: string;
  /** true when the row is kept because its field_type accumulates */
  union?: boolean;
}

type Layer = [string, string];

/**
 * Runs the cascade over the given layers (weakest first) and returns every row
 * that took part, effective and dropped alike, ordered interface -> report ->
 * brand / raw table, alphabetically inside each level.
 */
export function resolveObjects(
  objects: FetcherObject[],
  layers: Layer[],
): ResolvedObject[] {
  const byKey: { [key: string]: (ResolvedObject & { ord: number })[] } = {};
  const out: ResolvedObject[] = [];

  layers.forEach(([level, matchingId], order) => {
    if (!matchingId) return;
    objectsFor(objects, level, matchingId).forEach((row) => {
      const resolved = { ...row, ord: order };
      if (isDeprecatedKey(row.k)) {
        out.push({ ...row, shadow: true, why: "deprecated" });
      } else {
        byKey[row.k] = byKey[row.k] || [];
        byKey[row.k].push(resolved);
      }
    });
  });

  Object.keys(byKey)
    .sort()
    .forEach((key) => {
      const rows = byKey[key];
      // rule 4 - identical value at two levels is one row, credited to the narrower
      const keptByValue: { [value: string]: ResolvedObject & { ord: number } } =
        {};
      rows.forEach((row) => {
        const current = keptByValue[row.v];
        if (!current || row.ord > current.ord) keptByValue[row.v] = row;
      });

      // the drop wording is the query's, verbatim - it cannot name the winning level
      const why = isUnionKey(key)
        ? "duplicate value at a narrower level"
        : "overridden by a narrower level";
      rows.forEach((row) => {
        if (keptByValue[row.v] !== row) out.push({ ...row, shadow: true, why });
      });

      const unique = Object.keys(keptByValue).map((v) => keptByValue[v]);
      if (isUnionKey(key)) {
        // rule 3 - every level's row is sent
        unique.forEach((row) => out.push({ ...row, union: true }));
      } else {
        // rule 2 - the narrowest level wins outright
        const winningOrder = Math.max(...unique.map((row) => row.ord));
        unique.forEach((row) =>
          out.push(
            row.ord === winningOrder ? { ...row } : { ...row, shadow: true, why },
          ),
        );
      }
    });

  return out.sort((a, b) => {
    const byLevel = LEVEL_ORDER.indexOf(a.lvl) - LEVEL_ORDER.indexOf(b.lvl);
    if (byLevel !== 0) return byLevel;
    return a.k < b.k ? -1 : a.k > b.k ? 1 : 0;
  });
}

export const lookupName = (lookups: FetcherLookup[], id: string) =>
  lookups.find((row) => row.id === id)?.name ?? "";

/** The one-line "what this config does" summary shown in the Configs table. */
export function recipeText(
  config: FetcherConfig,
  cm: FetcherLookup[],
  sfm: FetcherLookup[],
) {
  return [
    config.report_name,
    lookupName(cm, config.cm) || "?",
    lookupName(sfm, config.sfm) || "?",
    config.ftn || "?",
    config.ctype || "?",
  ].join(" · ");
}

/** Configs sharing a report and the exact same recipe - a 100% duplicate. */
export const configTwins = (configs: FetcherConfig[], config: FetcherConfig) =>
  configs.filter(
    (row) =>
      row.id !== config.id &&
      row.report === config.report &&
      row.cm === config.cm &&
      row.sfm === config.sfm &&
      row.ft === config.ft,
  );

const normaliseReportName = (name: string) =>
  name.toLowerCase().replace(/[_-](api|selenium|gdrive|v\d+|new|old|update)$/g, "");

/** Reports on the same interface and collection type that look like copies. */
export const reportTwins = (reports: FetcherReport[], report: FetcherReport) =>
  reports.filter(
    (row) =>
      row.id !== report.id &&
      row.iface === report.iface &&
      row.ctype_id === report.ctype_id &&
      ((!!report.upath && !!row.upath && report.upath === row.upath) ||
        normaliseReportName(row.name) === normaliseReportName(report.name)),
  );
